import React, { Fragment, useCallback } from 'react';

import PropTypes from 'prop-types';

import { omit, size } from 'lodash';

import { accessApi } from 'utils/injectApi';

import { showIf } from '.';
import { AreaSubfield } from './AreaSubfield';
import { NumeroSubfield } from './NumeroSubfield';
import { TextAreaSubfield } from './TextAreaSubfield';

const Inputs = ({
	data,
	fields,
	onChangeHandler,
	errors,
	setErrors,
	qtdMinLinhas = 3,
	classNameContainer = '',
	definicao
}) => {
	/* REDUX */
	//const [errors, setErrors] = useErrors();

	const handleChangeValue = useCallback(
		e => {
			if (Array.isArray(e)) {
				const [valid] = e;
				e = valid;
			}
			const { name, value } = e.target || e;
			if (name === 'errors') {
				return;
			}
			setErrors(old => omit(old, [name]));
			onChangeHandler(value, name);
		},
		[onChangeHandler, setErrors]
	);

	const obtemApiData = async (data, id, endpoint, property) => {
		if (data?.[id]) {
			return false;
		}
		const response = await accessApi(endpoint);
		const apiData = response.data;

		let value = null;
		switch (property) {
			case 'ufm':
				if (apiData && typeof apiData === 'object') {
					value = apiData[property] || 0;
				} else {
					value = apiData || 0;
				}

				value = value.toFixed(4);

				value = {
					value
				};
				break;

			default:
				break;
		}

		handleChangeValue({ name: id, value });
	};

	const obtemValorMultiplicado = (data, id, operacao, decimals) => {
		let result = 1;

		for (const property of operacao) {
			let fator = data?.[property] || 0;
			if (typeof fator === 'object') {
				fator = fator.value;
				if (typeof fator === 'string') {
					fator = parseFloat(fator);
				}
			}
			result = result * fator;
		}
		const formattedValue = result.toFixed(decimals || 4);

		result = {
			value: formattedValue
		};
		if (data?.[id]?.value !== formattedValue) {
			handleChangeValue({ name: id, value: result });
		}
	};

	const obtemMultiplicador = (data, id, origem, perguntaOrigem) => {
		const option = perguntaOrigem.options.find(o => o.codigo === data?.[origem]);
		const multiplicador = option?.multiplicador || 0;
		let formattedValue = multiplicador.toFixed(4);
		if (data?.[id]?.value !== formattedValue) {
			handleChangeValue({ name: id, value: { value: formattedValue } });
		}
	};

	return (
		<div className="row">
			{fields.map(field => {
				const {
					id,
					label,
					type,
					unidade,
					obrigatorio: obrigatorioProps,
					readOnly,
					disabled,
					placeholder,
					endpoint,
					origem,
					property,
					decimals,
					operacao,
					classNameContainerInput = '',
					perguntaOrigem
				} = field;

				let obrigatorio = obrigatorioProps;
				if (typeof obrigatorio === 'object') {
					obrigatorio = showIf(data, definicao, obrigatorio, `obrigatorio ${id}`);
				}

				if (type === 'api') {
					obtemApiData(data, id, endpoint, property);
				}

				if (type === 'multiplicacao') {
					obtemValorMultiplicado(data, id, operacao, decimals);
				}

				if (type === 'multiplicador') {
					obtemMultiplicador(data, id, origem, perguntaOrigem);
				}

				return readOnly && data?.[id] ? (
					<Fragment key={id}>
						<div className="container-resposta-ap">
							<div className="label">
								{label}
								{obrigatorio && <span className="required">*</span>}
							</div>
							<span className="resposta-ap">{`${asString(data?.[id], type)}${unidade}`}</span>
						</div>
					</Fragment>
				) : (
					<Fragment key={id}>
						<div
							className={`${
								size(classNameContainer) === 0 && (type === 'string' || (fields.length === 1 && type !== 'number'))
									? 'col-md-12'
									: classNameContainerInput || 'col-md-6'
							} ${classNameContainer}`}
						>
							{(type === 'area' ||
								(type === 'api' && ['valor-ufm'].includes(id)) ||
								type === 'multiplicador' ||
								(type === 'multiplicacao' && ['valor-base-taxa', 'base-calculada', 'valor-taxa'].includes(id))) && (
								<AreaSubfield
									fieldName={id}
									placeholder={unidade ? unidade : null}
									label={label}
									value={data?.[id]}
									decimals={decimals || 2}
									errors={errors?.[id]}
									readOnly={type !== 'area' || readOnly}
									disabled={type !== 'area' || disabled}
									required={obrigatorio}
									onChangeHandler={type === 'api' ? () => null : e => handleChangeValue(e, type, 2)}
								/>
							)}
							{type === 'number' && (
								<NumeroSubfield
									fieldName={id}
									label={label}
									value={data?.[id]}
									errors={errors?.[id]}
									disabled={disabled}
									required={obrigatorio}
									onChangeHandler={e => handleChangeValue(e, type, 2)}
								/>
							)}
							{type === 'string' && (
								<TextAreaSubfield
									fieldName={id}
									label={label}
									value={data?.[id]}
									errors={errors?.[id]}
									qtdMinLinhas={qtdMinLinhas}
									required={obrigatorio}
									placeholder={placeholder}
									onChangeHandler={e => handleChangeValue(e, type)}
								/>
							)}
						</div>
					</Fragment>
				);
			})}
		</div>
	);
};
Inputs.displayName = 'Inputs';

Inputs.propTypes = {
	data: PropTypes.object,
	fields: PropTypes.arrayOf(PropTypes.object),
	setErrors: PropTypes.func,
	errors: PropTypes.object,
	onChangeHandler: PropTypes.func,
	qtdMinLinhas: PropTypes.number,
	classNameContainer: PropTypes.string,
	definicao: PropTypes.string
};
export default Inputs;

function asString(value, type) {
	if (typeof value === 'object') {
		value = value.value;
	}

	if (['area', 'number'].includes(type) && value === undefined) {
		return 'sem valor';
	}

	if (type === 'area') {
		return formatNumber(value, 2);
	}

	if (type === 'number') {
		return formatNumber(value, 0);
	}

	return value;
}

function formatNumber(string, decimais = 0) {
	string = `${string}`;
	string = string || '0';
	string = string.replace(/,/g, '.');
	let numberValue = parseFloat(string);

	const formattedNumber = new Intl.NumberFormat('pt-BR', {
		style: 'decimal',
		minimumFractionDigits: decimais,
		maximumFractionDigits: decimais,
		useGrouping: 'always'
	}).format(numberValue);

	return formattedNumber;
}
